import React, { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Popover,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCalendar,
  IconChevronRight,
  IconFilter,
  IconLetterCase,
  IconListCheck,
  IconPercentage,
  IconAlertTriangle,
  IconMapPin,
  IconUsers,
  IconClock,
  IconLink,
  IconCheckbox,
  IconHash,
  IconX,
} from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';
import type { GanttVisibleOptionalColumns } from './GanttGridColumnToggles';
import {
  getFilterableColumnDefs,
  type ColumnFilterValues,
  type GanttColumnDef,
  type GanttColumnFilterValue,
} from '../../../gantt/columns';
import { hasActiveColumnFilters } from '../../../gantt/columnFilters';
import { ganttToolbarButtonProps } from './ganttToolbarStyles';

export type GanttColumnFilterBarProps = {
  visibleOptionalColumns: GanttVisibleOptionalColumns;
  values: ColumnFilterValues;
  onChange: (next: ColumnFilterValues) => void;
  /** Dynamic select options keyed by column id (e.g. assignees from live data). */
  filterOptionsByColumnId?: Partial<
    Record<string, { value: string; label: string }[]>
  >;
};

const COLUMN_ICONS: Record<string, TablerIcon> = {
  srNo: IconHash,
  text: IconLetterCase,
  start_date: IconCalendar,
  end_date: IconCalendar,
  duration: IconClock,
  progress: IconPercentage,
  critical: IconAlertTriangle,
  taskType: IconListCheck,
  locationType: IconMapPin,
  predecessors: IconLink,
  unitActivityTracked: IconCheckbox,
  assignees: IconUsers,
};

function emptyValueFor(def: GanttColumnDef): GanttColumnFilterValue | undefined {
  if (!def.filter) return undefined;
  switch (def.filter.type) {
    case 'text':
      return { type: 'text', value: '' };
    case 'select':
      return { type: 'select', values: [] };
    case 'boolean':
      return { type: 'boolean', values: [] };
    case 'dateRange':
      return { type: 'dateRange', from: null, to: null };
    case 'numberRange':
      return { type: 'numberRange', min: null, max: null };
    default:
      return undefined;
  }
}

function isDefActive(def: GanttColumnDef, values: ColumnFilterValues): boolean {
  const v = values[def.id];
  return v != null && hasActiveColumnFilters({ [def.id]: v });
}

function setColumnValue(
  values: ColumnFilterValues,
  def: GanttColumnDef,
  next: GanttColumnFilterValue | undefined,
): ColumnFilterValues {
  const copy = { ...values };
  if (next == null || !hasActiveColumnFilters({ [def.id]: next })) {
    delete copy[def.id];
  } else {
    copy[def.id] = next;
  }
  return copy;
}

function CategoryDetailPanel({
  def,
  value,
  onChange,
  onClose,
}: {
  def: GanttColumnDef;
  value: GanttColumnFilterValue | undefined;
  onChange: (next: GanttColumnFilterValue | undefined) => void;
  onClose: () => void;
}) {
  const filter = def.filter;
  const [optionSearch, setOptionSearch] = useState('');
  if (!filter) return null;

  const current = value ?? emptyValueFor(def);
  const clearCategory = () => onChange(undefined);

  return (
    <div className="gantt-column-filter-detail">
      <div className="gantt-column-filter-detail-header">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap={8}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text size="sm" fw={600}>
              {def.label}
            </Text>
            {def.filterCaption ? (
              <Text size="xs" c="dimmed" mt={2} className="gantt-column-filter-detail-caption">
                {def.filterCaption}
              </Text>
            ) : null}
          </div>
          <ActionIcon
            type="button"
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={`Close ${def.label} filter`}
            onClick={onClose}
            style={{ flexShrink: 0 }}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </div>

      <div className="gantt-column-filter-detail-body">
        {filter.type === 'text' && current?.type === 'text' ? (
          <TextInput
            size="xs"
            placeholder={
              def.id === 'predecessors' || def.id === 'srNo' ? 'Exact Sr No' : 'Search'
            }
            value={current.value}
            onChange={(e) => onChange({ type: 'text', value: e.currentTarget.value })}
            autoFocus
          />
        ) : null}

        {filter.type === 'select' && current?.type === 'select' ? (
          <Stack gap={8} style={{ minHeight: 0, flex: 1 }}>
            <TextInput
              size="xs"
              placeholder="Search"
              value={optionSearch}
              onChange={(e) => setOptionSearch(e.currentTarget.value)}
            />
            <Stack gap={2} className="gantt-column-filter-option-list">
              {(filter.options ?? [])
                .filter((o) => {
                  const q = optionSearch.trim().toLowerCase();
                  if (!q) return true;
                  return o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q);
                })
                .map((o) => {
                  const checked = current.values.includes(o.value);
                  return (
                    <UnstyledButton
                      key={o.value}
                      type="button"
                      className="gantt-column-filter-option-row"
                      data-active={checked ? 'true' : undefined}
                      onClick={() => {
                        const next = checked
                          ? current.values.filter((v) => v !== o.value)
                          : [...current.values, o.value];
                        onChange({ type: 'select', values: next });
                      }}
                    >
                      <Checkbox
                        size="xs"
                        checked={checked}
                        readOnly
                        tabIndex={-1}
                        aria-hidden
                      />
                      <Text size="sm">{o.label}</Text>
                    </UnstyledButton>
                  );
                })}
            </Stack>
          </Stack>
        ) : null}

        {filter.type === 'boolean' && current?.type === 'boolean' ? (
          <Stack gap={2}>
            {(
              [
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
              ] as const
            ).map((o) => {
              const checked = current.values.includes(o.value);
              return (
                <UnstyledButton
                  key={String(o.value)}
                  type="button"
                  className="gantt-column-filter-option-row"
                  data-active={checked ? 'true' : undefined}
                  onClick={() => {
                    const next = checked
                      ? current.values.filter((v) => v !== o.value)
                      : [...current.values, o.value];
                    onChange({ type: 'boolean', values: next });
                  }}
                >
                  <Checkbox size="xs" checked={checked} readOnly tabIndex={-1} aria-hidden />
                  <Text size="sm">{o.label}</Text>
                </UnstyledButton>
              );
            })}
          </Stack>
        ) : null}

        {filter.type === 'dateRange' && current?.type === 'dateRange' ? (
          <Stack gap={8}>
            <TextInput
              size="xs"
              type="date"
              label="From"
              value={current.from ?? ''}
              onChange={(e) =>
                onChange({
                  type: 'dateRange',
                  from: e.currentTarget.value || null,
                  to: current.to,
                })
              }
            />
            <TextInput
              size="xs"
              type="date"
              label="To"
              value={current.to ?? ''}
              onChange={(e) =>
                onChange({
                  type: 'dateRange',
                  from: current.from,
                  to: e.currentTarget.value || null,
                })
              }
            />
          </Stack>
        ) : null}

        {filter.type === 'numberRange' && current?.type === 'numberRange' ? (
          <Stack gap={8}>
            <NumberInput
              size="xs"
              label="Min"
              placeholder="Min"
              value={current.min ?? undefined}
              onChange={(v) =>
                onChange({
                  type: 'numberRange',
                  min: typeof v === 'number' ? v : null,
                  max: current.max,
                })
              }
            />
            <NumberInput
              size="xs"
              label="Max"
              placeholder="Max"
              value={current.max ?? undefined}
              onChange={(v) =>
                onChange({
                  type: 'numberRange',
                  min: current.min,
                  max: typeof v === 'number' ? v : null,
                })
              }
            />
          </Stack>
        ) : null}
      </div>

      <div className="gantt-column-filter-detail-footer">
        <UnstyledButton
          type="button"
          className="gantt-column-filter-clear-link"
          onClick={clearCategory}
        >
          Clear
        </UnstyledButton>
      </div>
    </div>
  );
}

/** Toolbar Filter control — nested category popover (wireframe-style). */
export function GanttColumnFilterBar({
  visibleOptionalColumns,
  values,
  onChange,
  filterOptionsByColumnId,
}: GanttColumnFilterBarProps) {
  const [opened, setOpened] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const defs = useMemo(() => {
    return getFilterableColumnDefs(visibleOptionalColumns).map((def) => {
      const dynamicOptions = filterOptionsByColumnId?.[def.id];
      if (!dynamicOptions || !def.filter || def.filter.type !== 'select') return def;
      return {
        ...def,
        filter: {
          ...def.filter,
          options: dynamicOptions,
        },
      };
    });
  }, [visibleOptionalColumns, filterOptionsByColumnId]);

  const activeCount = useMemo(() => {
    if (!hasActiveColumnFilters(values)) return 0;
    return defs.filter((d) => isDefActive(d, values)).length;
  }, [defs, values]);

  const activeDef =
    activeCategoryId != null
      ? defs.find((d) => d.id === activeCategoryId) ?? null
      : null;

  if (defs.length === 0) return null;

  return (
    <Popover
      opened={opened}
      onChange={(next) => {
        setOpened(next);
        if (!next) setActiveCategoryId(null);
      }}
      position="bottom-start"
      shadow="md"
      radius="md"
      offset={8}
      withinPortal
    >
      <Popover.Target>
        <Button
          {...ganttToolbarButtonProps}
          variant={activeCount > 0 ? 'filled' : 'default'}
          color={activeCount > 0 ? 'brand' : undefined}
          type="button"
          leftSection={<IconFilter size={14} stroke={1.75} />}
          rightSection={
            activeCount > 0 ? (
              <Group gap={4} wrap="nowrap" align="center">
                <Badge size="xs" circle color="brand" variant="white">
                  {activeCount}
                </Badge>
                <ActionIcon
                  type="button"
                  component="span"
                  size={18}
                  radius="xl"
                  variant="transparent"
                  c="white"
                  aria-label="Clear all filters"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange({});
                    setActiveCategoryId(null);
                    setOpened(false);
                  }}
                  style={{ minWidth: 18, minHeight: 18, width: 18, height: 18 }}
                >
                  <IconX size={12} stroke={2.25} />
                </ActionIcon>
              </Group>
            ) : undefined
          }
          styles={{
            ...ganttToolbarButtonProps.styles,
            root: {
              ...ganttToolbarButtonProps.styles.root,
              ...(activeCount > 0 ? { paddingRight: 6 } : null),
            },
            section: { pointerEvents: 'all' },
          }}
          onClick={() => setOpened((o) => !o)}
          aria-label="Column filters"
          aria-expanded={opened}
        >
          Filter
        </Button>
      </Popover.Target>
      <Popover.Dropdown className="gantt-column-filter-popover" p={0}>
        <div
          className="gantt-column-filter-popover-panels"
          data-detail={activeDef ? 'true' : undefined}
        >
          <div className="gantt-column-filter-categories">
            <Group justify="space-between" align="center" px={12} pt={10} pb={6} wrap="nowrap">
              <Text size="sm" fw={600}>
                Filters
              </Text>
              {activeCount > 0 ? (
                <UnstyledButton
                  type="button"
                  className="gantt-column-filter-clear-link"
                  onClick={() => {
                    onChange({});
                    setActiveCategoryId(null);
                  }}
                >
                  Clear
                </UnstyledButton>
              ) : null}
            </Group>
            <Stack gap={0} px={6} pb={6}>
              {defs.map((def) => {
                const Icon = COLUMN_ICONS[def.id] ?? IconFilter;
                const active = isDefActive(def, values);
                const selected = activeCategoryId === def.id;
                return (
                  <UnstyledButton
                    key={def.id}
                    type="button"
                    className="gantt-column-filter-category-row"
                    data-active={selected ? 'true' : undefined}
                    onClick={() =>
                      setActiveCategoryId((prev) => (prev === def.id ? null : def.id))
                    }
                  >
                    <Group gap={8} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                      <Icon size={15} stroke={1.6} aria-hidden />
                      <Text size="sm" truncate style={{ minWidth: 0 }}>
                        {def.label}
                      </Text>
                    </Group>
                    <Group gap={6} wrap="nowrap">
                      {active ? (
                        <span className="gantt-column-filter-active-dot" aria-hidden />
                      ) : null}
                      <IconChevronRight size={14} stroke={1.6} aria-hidden />
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Stack>
            {activeCount > 0 ? (
              <div className="gantt-column-filter-categories-footer">
                <UnstyledButton
                  type="button"
                  className="gantt-column-filter-clear-link"
                  onClick={() => {
                    onChange({});
                    setActiveCategoryId(null);
                  }}
                >
                  Clear all filters
                </UnstyledButton>
              </div>
            ) : null}
          </div>

          {activeDef ? (
            <CategoryDetailPanel
              def={activeDef}
              value={values[activeDef.id]}
              onChange={(next) => onChange(setColumnValue(values, activeDef, next))}
              onClose={() => setActiveCategoryId(null)}
            />
          ) : null}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
